import React from 'react';
import { useTranslation } from 'react-i18next';
import { UnstyledButton } from '@/shared/ui';
import {
  CheckCircledIcon,
  CrossCircledIcon,
  CounterClockwiseClockIcon,
} from '@/shared/ui/radix-icons';
import type { Visit } from '@services/requests/housien-models/Visit';
import { useVisitDetailActions } from './useVisitDetailActions';
import { CancelDialog, FeedbackDialog } from './VisitDetailDialogs';

interface VisitDetailActionsProps {
  visit: Visit;
  agencyId: string;
  onEdit: () => void;
  /** Notifies the list that opened this detail, so its own copy is refetched. */
  onUpdated?: () => void;
}

/**
 * Contextual action bar that renders status-appropriate quick actions for a
 * visit, plus the cancel-with-reason dialog and feedback dialog.
 */
export function VisitDetailActions({ visit, agencyId, onEdit, onUpdated }: VisitDetailActionsProps) {
  const { t } = useTranslation('@pages/agencies');
  const actions = useVisitDetailActions({ visit, agencyId, onEdit, onUpdated });

  const status = visit.status ?? 'pending';
  if (status === 'done') return null;

  return (
    <>
      <div
        data-testid="visit-detail-actions"
        className="flex flex-wrap items-center gap-[var(--space-3)] mb-[var(--space-4)]"
      >
        {actions.canConfirm && (
          <ActionButton
            icon={<CheckCircledIcon width={14} height={14} />}
            label={t('visits.detail.actions.confirm', 'Confirmar')}
            variant="primary"
            onClick={actions.handleConfirm}
          />
        )}
        {actions.canMarkDone && (
          <ActionButton
            icon={<CheckCircledIcon width={14} height={14} />}
            label={t('visits.detail.actions.markDone', 'Marcar realizada')}
            variant="primary"
            onClick={actions.handleMarkDone}
            disabled={actions.isMarkDoneBlocked}
          />
        )}
        {actions.canReschedule && (
          <ActionButton
            icon={<CounterClockwiseClockIcon width={14} height={14} />}
            label={t('visits.detail.actions.reschedule', 'Reagendar')}
            variant="secondary"
            onClick={actions.handleReschedule}
          />
        )}
        {actions.canCancel && (
          <ActionButton
            icon={<CrossCircledIcon width={14} height={14} />}
            label={t('visits.detail.actions.cancel', 'Cancelar')}
            variant="danger"
            onClick={actions.handleCancel}
          />
        )}
      </div>

      <CancelDialog
        open={actions.cancelDialogOpen}
        onOpenChange={actions.setCancelDialogOpen}
        reason={actions.cancelReason}
        onReasonChange={actions.setCancelReason}
        onConfirm={actions.confirmCancel}
      />

      <FeedbackDialog
        open={actions.feedbackDialogOpen}
        onOpenChange={actions.setFeedbackDialogOpen}
        rating={actions.feedbackRating}
        onRatingChange={actions.setFeedbackRating}
        obs={actions.feedbackObs}
        onObsChange={actions.setFeedbackObs}
        onConfirm={actions.confirmFeedback}
      />
    </>
  );
}

interface ActionButtonProps {
  icon: React.ReactNode;
  label: string;
  variant: 'primary' | 'secondary' | 'danger';
  onClick: () => void;
  disabled?: boolean;
}

const VARIANT_CLASSES: Record<ActionButtonProps['variant'], string> = {
  primary: 'bg-[var(--seed-base)] text-[var(--text-inverse)] border border-transparent hover:opacity-[0.8]',
  secondary: 'bg-[var(--surface-secondary)] text-[var(--text-primary)] border border-[var(--border-subtle)] hover:bg-[var(--surface-tertiary)]',
  danger: 'bg-transparent text-[var(--status-error-text)] border border-[var(--status-error-border)] hover:bg-[var(--status-error-bg)]',
};

/**
 * Polymorphic action button with variant-driven styling.
 */
function ActionButton({ icon, label, variant, onClick, disabled }: ActionButtonProps) {
  return (
    <UnstyledButton
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`flex items-center gap-[var(--space-2)] px-[var(--space-4)] py-[var(--space-2)] text-[var(--text-body-sm)] font-[var(--font-medium)] rounded-(--radius-md) cursor-pointer transition-[opacity,background-color] duration-[var(--transition-standard)] ${VARIANT_CLASSES[variant]} disabled:opacity-50 disabled:cursor-not-allowed`}
    >
      {icon}
      {label}
    </UnstyledButton>
  );
}
